import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Link,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { signIn, currentUser, loading: authLoading } = useFirebase();
  const { checkSubscription, isLoading: subscriptionLoading } = useSubscription();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redirecionar se já estiver autenticado
  React.useEffect(() => {
    if (currentUser && !authLoading && !subscriptionLoading) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from);
    }
  }, [currentUser, authLoading, subscriptionLoading, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }
    
    try {
      setIsProcessing(true);
      setError(null);
      
      // Fazer login com Firebase
      await signIn(email, password);
      
      // Verificar status da assinatura
      const isSubscriptionValid = await checkSubscription();
      
      // Redirecionar com base no status da assinatura
      if (isSubscriptionValid) {
        const from = (location.state as any)?.from?.pathname || '/';
        navigate(from);
      } else {
        navigate('/planos');
      }
    } catch (err: any) {
      console.error('Erro no login:', err);
      
      // Mensagens de erro personalizadas com base no código de erro do Firebase
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Email ou senha incorretos. Por favor, tente novamente.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Muitas tentativas de login. Por favor, tente novamente mais tarde ou redefina sua senha.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Email inválido. Por favor, verifique e tente novamente.');
      } else {
        setError(err.message || 'Ocorreu um erro ao fazer login. Por favor, tente novamente.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5">
            Login
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isProcessing}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Senha"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isProcessing}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isProcessing || authLoading || subscriptionLoading}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'Entrar'}
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Esqueceu a senha?
              </Link>
              <Link component={RouterLink} to="/register" variant="body2">
                Não tem uma conta? Cadastre-se
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;